import { isObject } from '../common/utils/object-utils'

/**
 * The panel's verdict on one prop value — `formUtils.buildSchema` checked
 * against `getDefaultValueForStep` — which the server cannot import. Pinned
 * to the real panel by react-ui
 * `piece-properties/test/prop-value-parity.test.tsx`; stricter only where
 * that test lists a documented divergence, with its reason.
 */

/** Minimal shape of a piece property needed to judge one of its values */
export type PropValueShape = {
    type: string
    required?: boolean
    defaultValue?: unknown
    maxLength?: number
    properties?: Record<string, PropValueShape>
}

/** The one blank predicate: `null`, `undefined` or `''` — nothing else */
export const isBlankValue = (value: unknown): boolean =>
    value === null || value === undefined || value === ''

export type PropValueVerdict = 'accepted' | 'missing' | 'invalid'

const isTextLike = (value: unknown, minLength: number, maxLength?: number): boolean =>
    typeof value === 'string' &&
    value.length >= minLength &&
    (maxLength === undefined || value.length <= maxLength)

// [] and {} are what a form default looks like for an unfilled ARRAY/OBJECT prop
export const isEmptyCollection = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length === 0
    return isObject(value) && Object.keys(value).length === 0
}

/**
 * Blank-or-missing-shaped values the panel itself would accept once
 * defaulted, but that never configure a required prop: a single choice at
 * `[]`/`{}`, an empty multi-select, a DYNAMIC prop holding no key (it would
 * send nothing). Plain blank (`null`/`undefined`/`''`) always qualifies.
 */
const isMissingForRequired = (prop: PropValueShape, value: unknown): boolean => {
    if (isBlankValue(value)) return true
    if (
        (prop.type === 'STATIC_DROPDOWN' || prop.type === 'DROPDOWN') &&
        isEmptyCollection(value)
    ) {
        return true
    }
    if (
        (prop.type === 'MULTI_SELECT_DROPDOWN' ||
            prop.type === 'STATIC_MULTI_SELECT_DROPDOWN') &&
        Array.isArray(value) &&
        value.length === 0
    ) {
        return true
    }
    if (
        prop.type === 'DYNAMIC' &&
        isObject(value) &&
        Object.keys(value).length === 0
    ) {
        return true
    }
    return false
}

/**
 * Whether `value`, taken exactly as given, matches the panel's per-field
 * schema for every type except ARRAY (judged separately: an item, unlike a
 * scalar, needs its own aggregation of accepted/missing/invalid). Mirrors
 * `formUtils.buildSchema` — including its generic null/undefined fallback for
 * every optional type, and DYNAMIC's one exception to it: no string fallback,
 * required or not.
 */
const matchesPanelType = (prop: PropValueShape, value: unknown): boolean => {
    const required = prop.required ?? false
    const minLength = required ? 1 : 0
    const nullableWhenOptional = !required && (value === null || value === undefined)
    switch (prop.type) {
        case 'MARKDOWN':
            return true
        case 'SHORT_TEXT':
        case 'LONG_TEXT':
            return nullableWhenOptional || isTextLike(value, minLength, prop.maxLength)
        case 'DATE_TIME':
        case 'FILE':
            return nullableWhenOptional || isTextLike(value, minLength)
        case 'CHECKBOX':
            return (
                nullableWhenOptional ||
                typeof value === 'boolean' ||
                isTextLike(value, minLength)
            )
        case 'NUMBER':
            return (
                nullableWhenOptional ||
                typeof value === 'number' ||
                isTextLike(value, minLength)
            )
        case 'STATIC_DROPDOWN':
        case 'DROPDOWN':
            // The panel's schema is "anything but null/undefined" — stricter
            // on '' and empty collections is applied at the required layer,
            // never here: this is the panel's own verdict
            return !required || (value !== null && value !== undefined)
        case 'MULTI_SELECT_DROPDOWN':
        case 'STATIC_MULTI_SELECT_DROPDOWN':
            // No minItems, ever — an empty selection is handled at the
            // required layer too
            return nullableWhenOptional || Array.isArray(value) || isTextLike(value, minLength)
        case 'OBJECT':
            return nullableWhenOptional || isObject(value) || isTextLike(value, minLength)
        case 'JSON':
            return (
                nullableWhenOptional ||
                isObject(value) ||
                Array.isArray(value) ||
                isTextLike(value, minLength)
            )
        case 'DYNAMIC':
            // No string fallback at all, required or not — an unresolved
            // template in a DYNAMIC prop is not an object
            return nullableWhenOptional || isObject(value)
        default:
            // A type the panel's switch does not know either: its wrap alone
            // decides — anything when optional, any filled value when required
            return !required || !isBlankValue(value)
    }
}

/**
 * One list item against its declared sub-properties — the panel's nested
 * `buildSchema`, run on the item exactly as written: no default is filled
 * into an item (`getDefaultValueForStep` only ever runs on the step's own
 * input). A sub-field follows the very same per-field rule, so an optional
 * one is checked too — not only a required one.
 */
const judgeListItem = (
    properties: Record<string, PropValueShape>,
    item: unknown,
): PropValueVerdict => {
    if (!isObject(item)) return 'invalid'
    const verdicts = Object.entries(properties).map(([key, subProp]) =>
        judgeGiven(subProp, item[key]),
    )
    if (verdicts.every((verdict) => verdict === 'accepted')) return 'accepted'
    return verdicts.includes('invalid') ? 'invalid' : 'missing'
}

/**
 * ARRAY is judged apart from `matchesPanelType`: a required empty list is
 * `missing` (minItems 1), and a list of objects aggregates its items'
 * verdicts rather than collapsing to one boolean.
 */
const judgeArrayShape = (prop: PropValueShape, value: unknown): PropValueVerdict => {
    const required = prop.required ?? false
    const minLength = required ? 1 : 0
    if (typeof value === 'string') {
        return isTextLike(value, minLength) ? 'accepted' : 'invalid'
    }
    if (!Array.isArray(value)) return 'invalid'
    if (required && value.length === 0) return 'missing'
    if (!prop.properties) {
        return value.every((item) => isTextLike(item, minLength)) ? 'accepted' : 'invalid'
    }
    const properties = prop.properties
    const itemVerdicts = value.map((item) => judgeListItem(properties, item))
    if (itemVerdicts.every((verdict) => verdict === 'accepted')) return 'accepted'
    return itemVerdicts.includes('invalid') ? 'invalid' : 'missing'
}

/**
 * The verdict for a value exactly as given — no default substitution. The
 * one judge used both at the top level (after `appliedDefault`) and for a
 * list item's sub-fields (which the panel never defaults): the only
 * difference between the two call sites is that one step.
 */
const judgeGiven = (prop: PropValueShape, value: unknown): PropValueVerdict => {
    // The panel's MARKDOWN schema stays `Unknown` whatever `required` says —
    // an info panel is never a value to judge
    if (prop.type === 'MARKDOWN') return 'accepted'
    const required = prop.required ?? false
    if (required && isMissingForRequired(prop, value)) return 'missing'
    if (prop.type === 'ARRAY') return judgeArrayShape(prop, value)
    return matchesPanelType(prop, value) ? 'accepted' : 'invalid'
}

/**
 * The piece's default, exactly as `getDefaultValueForStep` fills it in: only
 * `undefined` takes it. `null` is deliberately NOT folded into "absent" for a
 * required prop — except OBJECT, which follows the panel (its `??` treats
 * `null` like `undefined`, and an absent OBJECT is a settled value). A
 * required DYNAMIC's default is withheld on purpose, declared or not: the
 * walker never reads a DYNAMIC prop's own top-level default, only its
 * resolved sub-fields', so there is nothing to write here (the parity test
 * documents this divergence).
 *
 * Exported so the walker can materialise the exact value it judges accepted:
 * the engine applies no default of its own at run time (`props-processor.ts`),
 * so a required DYNAMIC sub-field settled only by this default must be
 * written, or the run sends nothing for it.
 */
export const appliedDefault = (prop: PropValueShape, value: unknown): unknown => {
    const required = prop.required ?? false
    if (value === null && required && prop.type !== 'OBJECT') return null
    if (value !== undefined && value !== null) return value
    switch (prop.type) {
        case 'CHECKBOX':
            return prop.defaultValue ?? false
        case 'ARRAY':
            return prop.defaultValue ?? []
        case 'OBJECT':
            return prop.defaultValue ?? {}
        case 'DYNAMIC':
            return required ? value : (prop.defaultValue ?? {})
        default:
            return prop.defaultValue
    }
}

/** The panel's verdict on one prop value, as written in the step's input. */
export const judgePropValue = (prop: PropValueShape, value: unknown): PropValueVerdict =>
    judgeGiven(prop, appliedDefault(prop, value))

/** Whether the prop lets the step run with this value. */
export const isPropValueAccepted = (prop: PropValueShape, value: unknown): boolean =>
    judgePropValue(prop, value) === 'accepted'
